<template>
  <!-- Solo en builds Capacitor; en web/produccion no se renderiza nada. -->
  <!-- Se enciende desde el menu de diagnostico: 5 taps en la version del sidebar. -->
  <div v-if="isCapacitor">
    <template v-if="logStore.enabled">
      <q-btn
        @click="open = true"
        class="log-tracker-fab"
        icon="bug_report"
        color="grey-9"
        size="sm"
        round
      >
        <q-badge v-if="errorCount" color="red" floating>
          {{ errorCount }}
        </q-badge>
        <q-badge v-else-if="totalCount" color="grey-7" floating>
          {{ totalCount }}
        </q-badge>
      </q-btn>

      <q-dialog v-model="open" position="bottom" full-width>
        <q-card class="column log-tracker-card">
          <q-card-section class="row items-center q-py-sm q-px-sm bg-grey-9 text-white">
            <q-tabs
              v-model="tab"
              class="col"
              active-color="amber"
              indicator-color="amber"
              dense
              narrow-indicator
              no-caps
            >
              <q-tab :label="`Consola (${filteredEntries.length})`" name="console" />
              <q-tab :label="`Red (${filteredRequests.length})`" name="network" />
            </q-tabs>
            <q-btn icon="close" dense flat v-close-popup />
          </q-card-section>

          <q-separator />

          <!-- Barra de acciones: cambia segun la pestana activa. -->
          <q-card-section class="row items-center q-py-xs q-px-sm bg-grey-8 text-white">
            <template v-if="tab === 'console'">
              <q-btn :color="onlyPush ? 'amber' : 'white'" @click="onlyPush = !onlyPush" icon="filter_alt" dense flat>
                <q-tooltip>{{ onlyPush ? 'Ver todo' : 'Solo [PUSH]' }}</q-tooltip>
              </q-btn>
            </template>
            <template v-else>
              <q-btn :color="onlyFailed ? 'amber' : 'white'" @click="onlyFailed = !onlyFailed" icon="error_outline" dense flat>
                <q-tooltip>{{ onlyFailed ? 'Ver todo' : 'Solo fallidas' }}</q-tooltip>
              </q-btn>
              <q-btn
                :color="netStore.paused ? 'amber' : 'white'"
                :icon="netStore.paused ? 'play_arrow' : 'pause'"
                @click="netStore.paused = !netStore.paused"
                dense
                flat
              >
                <q-tooltip>{{ netStore.paused ? 'Reanudar captura' : 'Pausar captura' }}</q-tooltip>
              </q-btn>
            </template>
            <q-space />
            <q-btn @click="copyAll" icon="content_copy" dense flat>
              <q-tooltip>Copiar todo</q-tooltip>
            </q-btn>
            <q-btn @click="clear" icon="delete" dense flat>
              <q-tooltip>Limpiar</q-tooltip>
            </q-btn>
            <q-btn @click="hideTracker" icon="visibility_off" dense flat>
              <q-tooltip>Ocultar tracker</q-tooltip>
            </q-btn>
          </q-card-section>

          <q-separator />

          <q-card-section class="col scroll q-pa-none">
            <!-- Consola -->
            <template v-if="tab === 'console'">
              <div
                v-for="entry in filteredEntries"
                :key="entry.id"
                :class="`log-${entry.level}`"
                class="log-line"
              >
                <span class="log-time">{{ entry.time }}</span>
                <span class="log-text">{{ entry.text }}</span>
              </div>
              <div v-if="!filteredEntries.length" class="text-center text-grey q-pa-md">
                Sin registros todavia
              </div>
            </template>

            <!-- Red -->
            <template v-else>
              <div class="net-env">
                {{ envLine }}
              </div>
              <div v-for="request in filteredRequests" :key="request.id" class="net-row">
                <div @click="toggleRequest(request.id)" class="net-head">
                  <span :class="`net-status net-status--${statusTone(request)}`">
                    {{ statusLabel(request) }}
                  </span>
                  <span class="net-method">{{ request.method }}</span>
                  <span class="net-url">{{ request.shortUrl }}</span>
                  <span class="net-meta">{{ durationLabel(request) }}</span>
                </div>

                <div v-if="expandedId === request.id" class="net-detail">
                  <pre class="net-pre">{{ requestDetail(request) }}</pre>
                  <q-btn
                    @click="copyRequest(request)"
                    label="Copiar esta peticion"
                    icon="content_copy"
                    size="sm"
                    color="grey-8"
                    dense
                    flat
                  />
                </div>
              </div>
              <div v-if="!filteredRequests.length" class="text-center text-grey q-pa-md">
                Sin peticiones todavia
              </div>
            </template>
          </q-card-section>
        </q-card>
      </q-dialog>
    </template>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core'
import { envSummary, envHeader } from '@/utils/envInfo'
import { logStore, clearLogs } from 'src/utils/logTracker'
import { netStore, clearRequests, formatRequest } from 'src/utils/networkTracker'

export default {
  name: 'LogTracker',
  data () {
    return {
      open: false,
      tab: 'console',
      onlyPush: false,
      onlyFailed: false,
      expandedId: null,
      logStore,
      netStore
    }
  },
  computed: {
    isCapacitor () {
      return Capacitor.isNativePlatform()
    },
    filteredEntries () {
      const list = this.onlyPush
        ? this.logStore.entries.filter(e => e.text.includes('[PUSH]'))
        : this.logStore.entries
      // Mas reciente arriba.
      return list.slice().reverse()
    },
    filteredRequests () {
      const list = this.onlyFailed
        ? this.netStore.entries.filter(this.isFailed)
        : this.netStore.entries
      return list.slice().reverse()
    },
    // Solo lo que ya se sabe que salio mal. Una peticion en vuelo cuenta como fallida
    // para el FILTRO (ahi si interesa ver las colgadas), pero no para el badge: si no,
    // la burbuja se pinta de rojo con cualquier request normal en curso y el color
    // deja de significar algo.
    errorCount () {
      const logErrors = this.logStore.entries.filter(e => e.level === 'error').length
      return logErrors + this.netStore.entries.filter(r => !r.pending && this.isFailed(r)).length
    },
    totalCount () {
      return this.logStore.entries.length + this.netStore.entries.length
    },
    envLine () {
      return envSummary()
    }
  },
  methods: {
    hideTracker () {
      this.open = false
      this.logStore.enabled = false
    },
    clear () {
      if (this.tab === 'console') clearLogs()
      else clearRequests()
    },
    isFailed (request) {
      return request.pending || request.error !== null || request.status === null || request.status >= 400
    },
    statusTone (request) {
      if (request.pending) return 'pending'
      if (request.status === null) return 'fail'
      if (request.status >= 500) return 'fail'
      if (request.status >= 400) return 'warn'
      if (request.status >= 300) return 'info'
      return 'ok'
    },
    statusLabel (request) {
      if (request.pending) return '...'
      return request.status === null ? 'ERR' : String(request.status)
    },
    durationLabel (request) {
      if (request.durationMs === null) return 'en vuelo'
      return request.durationMs >= 1000
        ? `${(request.durationMs / 1000).toFixed(2)}s`
        : `${request.durationMs}ms`
    },
    toggleRequest (id) {
      this.expandedId = this.expandedId === id ? null : id
    },
    requestDetail (request) {
      return formatRequest(request)
    },
    copyRequest (request) {
      this.copyToClipboard(formatRequest(request))
    },
    copyAll () {
      const body = this.tab === 'console'
        ? this.logStore.entries.map(e => `${e.time} [${e.level}] ${e.text}`).join('\n')
        : this.netStore.entries.map(formatRequest).join('\n\n----------\n\n')
      this.copyToClipboard(`${envHeader()}\n\n==========\n\n${body}`)
    },
    copyToClipboard (text) {
      const done = () => this.$q.notify({ type: 'positive', message: 'Copiado', timeout: 1000 })
      const fail = () => this.$q.notify({ type: 'negative', message: 'No se pudo copiar', timeout: 1000 })
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(fail)
      } else {
        fail()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.log-tracker-fab {
  position: fixed;
  right: 12px;
  bottom: 90px;
  z-index: 9999;
  opacity: 0.85;
}
.log-tracker-card {
  max-height: 80vh;
}
.log-line {
  font-family: monospace;
  font-size: 11px;
  line-height: 1.35;
  padding: 2px 8px;
  border-bottom: 1px solid #eee;
  white-space: pre-wrap;
  word-break: break-all;
}
.log-time {
  color: #888;
  margin-right: 6px;
}
.log-warn {
  background: #fff8e1;
}
.log-error {
  background: #ffebee;
  color: #b71c1c;
}

.net-env {
  padding: 4px 8px;
  background: #eceff1;
  border-bottom: 1px solid #cfd8dc;
  font-family: monospace;
  font-size: 10px;
  color: #37474f;
  word-break: break-all;
}
.net-row {
  border-bottom: 1px solid #eee;
}
.net-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  font-family: monospace;
  font-size: 11px;
}
.net-status {
  flex: 0 0 auto;
  min-width: 34px;
  text-align: center;
  border-radius: 3px;
  padding: 1px 3px;
  font-weight: bold;
  color: #fff;

  &--ok { background: #2e7d32; }
  &--info { background: #0277bd; }
  &--warn { background: #ef6c00; }
  &--fail { background: #b71c1c; }
  &--pending { background: #757575; }
}
.net-method {
  flex: 0 0 auto;
  color: #555;
}
.net-url {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.net-meta {
  flex: 0 0 auto;
  color: #888;
}
.net-detail {
  background: #fafafa;
  padding: 6px 8px;
}
.net-pre {
  margin: 0 0 6px;
  font-family: monospace;
  font-size: 10px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
